// Design único dos gráficos do Dashboard (Comercial, Meu espaço, Marketing e os
// widgets): degradê sob a linha, barras e arcos arredondados, pouca grade,
// balão em "pílula" e cores que acompanham o tema claro/escuro.
//
// Uso típico numa seção:
//   const theme = useChartTheme();
//   collection: { labels, datasets: [lineDataset({ data, label, theme })] }
//   options:    computed(() => lineOptions({ theme: theme.value, formatTick }))
//
// `dot` é a classe Tailwind LITERAL da mesma cor, para legendas em HTML (sem
// estilo inline).
export const CHART_PALETTE = [
  { hex: '#7C5CFC', dot: 'bg-[#7C5CFC]' },
  { hex: '#22D3EE', dot: 'bg-[#22D3EE]' },
  { hex: '#34D399', dot: 'bg-[#34D399]' },
  { hex: '#FBBF24', dot: 'bg-[#FBBF24]' },
  { hex: '#38BDF8', dot: 'bg-[#38BDF8]' },
  { hex: '#FB7185', dot: 'bg-[#FB7185]' },
  { hex: '#818CF8', dot: 'bg-[#818CF8]' },
  { hex: '#FB923C', dot: 'bg-[#FB923C]' },
];

// "Outros" fica em cinza para não competir com os grupos de verdade.
export const OTHERS_COLOR = { hex: '#94A3B8', dot: 'bg-[#94A3B8]' };

export const PRIMARY = CHART_PALETTE[0].hex;
export const SECONDARY = CHART_PALETTE[1].hex;
export const ACCENT = CHART_PALETTE[3].hex;

const FONT = { size: 11 };
const MAX_DOTS = 14;
const DEFAULT_LABEL_LENGTH = 16;

export const paletteFor = index => CHART_PALETTE[index % CHART_PALETTE.length];

export const withAlpha = (hex, alpha) => {
  const value = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map(start =>
    parseInt(value.slice(start, start + 2), 16)
  );
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// As cores do tema são variáveis CSS no formato "R G B" e o canvas precisa do
// valor pronto; o `fallback` cobre quando a variável não existe.
export const themeColor = (name, fallback, alpha = 1) => {
  const raw =
    typeof document === 'undefined'
      ? ''
      : getComputedStyle(document.documentElement).getPropertyValue(name);
  const [r, g, b] = (raw.trim() || fallback).split(/\s+/);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Balão arredondado no estilo "pílula", igual em todos os gráficos.
export const tooltipStyle = (callbacks, color = PRIMARY) => ({
  backgroundColor: color,
  titleColor: '#FFFFFF',
  bodyColor: '#FFFFFF',
  padding: { x: 10, y: 6 },
  cornerRadius: 8,
  caretSize: 5,
  displayColors: false,
  titleFont: { size: 11, weight: 'normal' },
  bodyFont: { size: 12, weight: '600' },
  callbacks,
});

// Degradê vertical sob a linha: forte no topo e transparente na base.
export const areaGradient =
  (color = PRIMARY) =>
  ({ chart }) => {
    const { ctx, chartArea } = chart;
    if (!chartArea) return withAlpha(color, 0.15);
    const gradient = ctx.createLinearGradient(
      0,
      chartArea.top,
      0,
      chartArea.bottom
    );
    gradient.addColorStop(0, withAlpha(color, 0.4));
    gradient.addColorStop(1, withAlpha(color, 0));
    return gradient;
  };

// Degradê das barras (violeta → ciano). Na horizontal vai da esquerda para a
// direita; na vertical, da base para o topo. `isMuted(index)` pinta de cinza
// (ex.: a barra "Outros").
export const barFill =
  ({ horizontal = true, isMuted = () => false } = {}) =>
  ({ chart, dataIndex }) => {
    if (isMuted(dataIndex)) return OTHERS_COLOR.hex;
    const { ctx, chartArea } = chart;
    if (!chartArea) return PRIMARY;
    const gradient = horizontal
      ? ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0)
      : ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
    gradient.addColorStop(0, withAlpha(PRIMARY, 0.9));
    gradient.addColorStop(1, SECONDARY);
    return gradient;
  };

// Linha suave com área em degradê. Em séries curtas mostra os pontos; o último
// ponto sempre fica em destaque. `theme` é o computed de `useChartTheme`.
export const lineDataset = ({
  data,
  label,
  theme,
  color = PRIMARY,
  fill = true,
}) => {
  // O destaque vai para o último ponto COM valor (a série pode terminar em null).
  const last = data.findLastIndex(
    value => value !== null && value !== undefined
  );
  const showDots = data.length <= MAX_DOTS;
  return {
    label,
    data,
    borderColor: color,
    borderWidth: 2.5,
    backgroundColor: fill ? areaGradient(color) : color,
    fill,
    tension: 0.4,
    cubicInterpolationMode: 'monotone',
    pointRadius: ({ dataIndex }) => {
      if (dataIndex === last) return 5;
      return showDots ? 3.5 : 0;
    },
    pointHoverRadius: 6,
    pointBorderWidth: 2,
    pointBorderColor: color,
    pointBackgroundColor: ({ dataIndex }) =>
      dataIndex === last ? color : theme.value.surface,
  };
};

export const barDataset = ({
  data,
  label,
  horizontal = true,
  isMuted,
  color,
}) => ({
  label,
  data,
  backgroundColor: color || barFill({ horizontal, isMuted }),
  borderRadius: 6,
  borderSkipped: false,
  maxBarThickness: horizontal ? 16 : 28,
});

export const doughnutDataset = ({ data, colors }) => ({
  data,
  backgroundColor: colors,
  borderWidth: 0,
  spacing: 3,
  borderRadius: 6,
  hoverOffset: 4,
});

// Blocos de eixo para montar `scales` (inclusive com eixo duplo, como no Marketing).
export const categoryAxis = (theme, extra = {}) => ({
  grid: { display: false },
  border: { display: false },
  ticks: { color: theme.text, font: FONT, maxTicksLimit: 6, maxRotation: 0 },
  ...extra,
});

export const valueAxis = (theme, formatTick, extra = {}) => ({
  beginAtZero: true,
  grid: { color: theme.grid, drawTicks: false },
  border: { display: false },
  ticks: {
    color: theme.text,
    font: FONT,
    maxTicksLimit: 5,
    padding: 8,
    callback: formatTick,
  },
  ...extra,
});

// Abrevia nomes longos no eixo (o nome completo continua no balão).
export const truncateTick = (max = DEFAULT_LABEL_LENGTH) =>
  function truncate(value) {
    const label = this.getLabelForValue(value);
    return label.length > max ? `${label.slice(0, max - 1)}…` : label;
  };

// Legenda do Chart.js no topo à direita, com marcadores redondos.
export const legendStyle = theme => ({
  display: true,
  position: 'top',
  align: 'end',
  labels: {
    usePointStyle: true,
    boxWidth: 8,
    boxHeight: 8,
    color: theme.text,
    font: FONT,
  },
});

export const lineOptions = ({ theme, formatTick, legend = false }) => ({
  layout: { padding: { top: 8, right: 8 } },
  interaction: { mode: 'index', intersect: false },
  scales: {
    x: categoryAxis(theme),
    y: valueAxis(theme, formatTick),
  },
  plugins: {
    legend: legend ? legendStyle(theme) : { display: false },
    tooltip: tooltipStyle({ label: context => formatTick(context.parsed.y) }),
  },
});

// Barras horizontais (padrão): o nome de cada grupo fica à esquerda e todos
// aparecem. Na vertical (`horizontal: false`) usa o eixo de categorias embaixo.
export const barOptions = ({
  theme,
  formatTick,
  horizontal = true,
  legend = false,
  labelLength,
}) => {
  const axis = horizontal ? 'x' : 'y';
  return {
    indexAxis: horizontal ? 'y' : 'x',
    layout: { padding: { top: 8, right: 8 } },
    datasets: { bar: { barPercentage: 0.7, categoryPercentage: 0.85 } },
    scales: horizontal
      ? {
          x: valueAxis(theme, formatTick),
          y: categoryAxis(theme, {
            ticks: {
              color: theme.text,
              font: FONT,
              autoSkip: false,
              callback: truncateTick(labelLength),
            },
          }),
        }
      : {
          x: categoryAxis(theme),
          y: valueAxis(theme, formatTick),
        },
    plugins: {
      legend: legend ? legendStyle(theme) : { display: false },
      tooltip: tooltipStyle({
        label: context => formatTick(context.parsed[axis]),
      }),
    },
  };
};

// A legenda da rosca é HTML ao lado do gráfico, então a do Chart.js fica desligada.
export const doughnutOptions = ({ formatTick }) => ({
  cutout: '72%',
  plugins: {
    legend: { display: false },
    tooltip: {
      ...tooltipStyle({
        label: context => `${context.label}: ${formatTick(context.raw)}`,
      }),
      displayColors: true,
    },
  },
});
